import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import StairsOutlinedIcon from '@mui/icons-material/StairsOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { exportDesignTicketReportPDF } from 'components/Common/EntityTicketPdf';
import { reasonPhrase } from 'components/ExportDesign/exportDesignControl';
import 'resources/styles/export-design-ticket.css';

// --- The Design Assistant's saved ticket.
//
// DESIGN MOCKUP, frontend only. Nothing here talks to an API: the ticket renders from a
// payload prop, and the preview route feeds it SAMPLE_TICKET below. "Save Result" is still
// the stub in ExportDesignChatUI (handleSaveResult) — this exists so the Save Result
// decision can be taken against something concrete rather than described.
//
// Modelled on the Beneficial Ownership saved ticket
// (components/Monitor/ResultsTab/BeneficialOwnershipTicketResults.tsx + index.css:49316-50110),
// which is the most developed ticket in the app. Its paired-panel structure suits this tool
// because the content is naturally paired — specs against determination, open items against
// grounding, options against ladder — where the Classify ticket is a transcript beside a report.
//
// Every field below maps to something the tool already holds, EXCEPT the three marked
// `proposed`, which would need backend work and are labelled as such on the page.
//
// STRUCTURE FOLLOWS THE GUIDED FLOW (the four named stages in the progress panel):
//   1. Product Specs & Use  -> "Product specs & use"
//   2. Classify Export      -> "Classification" (+ open items)
//   3. Specification Map    -> "Specification map" (which specs drive which control)
//   4. Final Design Summary -> THE TICKET ITSELF: a saved ticket is the saved summary, so it is not
//      repeated as a panel or tile of its own. Its summary and recommended next step lead the page
//      (hero), and what the report adds (caveats, options, ladder) follows when it was generated.
// Licensing, design changes, design options and the regime ladder are NOT stages. They appear only
// when a check or analysis actually ran (asked for, or run by the report), never as empty headed
// panels — see the conditionals in the component and `readDesignTicket`.

export interface TicketSpec {
  label: string;
  value: string;
  /** Provenance. The fact ledger stores {value, source, version, updated_at} per spec
   *  (export_design_facts.py set_spec); the report payload currently flattens it away. */
  source?: string;
}

export interface TicketConstraint {
  citation: string;
  matchedSpec: string;
  decontrol: string;
  controlText: string;
}

export interface TicketOpenItem {
  missingSpec: string;
  assumed: string;
  impact: string;
  /** True when the real value could push the product into a MORE controlled position. */
  couldRaise?: boolean;
}

export interface TicketOption {
  label: string;
  targetRegime: string;
  changeSummary: string;
  feasibility?: string;
  tradeoff?: string;
  recommended?: boolean;
}

/** A design-change analysis the user ASKED for ("what would move it to EAR99?"). It is not a stage
 *  of the guided flow any more, so it only exists in a saved assessment when it was requested. */
export interface TicketDesignChanges {
  target: string;
  reachable: boolean;
  /** Why it is reachable, or — when it isn't — the honest reason. */
  summary?: string;
  changes: Array<{ description: string; details: string[] }>;
  resultingRegime?: string;
  cost?: string;
  residual: string[];
}

export interface TicketRung {
  regime: string;
  isCurrent?: boolean;
  reachable?: boolean;
  note: string;
}

export interface TicketChatMessage {
  sender: 'user' | 'assistant';
  text: string;
  /** ISO time the message was sent; '' when it was not recorded. */
  ts: string;
  /** Files attached to a user message. */
  files?: string[];
  /** The turn that delivered the Final Design Summary, which the ticket already shows in full above. */
  isReport?: boolean;
}

export interface DesignTicketPayload {
  ticketName: string;
  reference?: string;
  status: string;
  owner: string;
  team: string;
  savedAt: string;
  sharedWith: number;
  /** Export Workflow's own vocabulary (ConsolidatedResults.tsx's metadata strip) — reused
   *  verbatim, not renamed, so the two sibling tools' tickets read as one family. */
  frequency: string;
  ticketType: string;
  productName: string;
  /** The saved case's own id — what "Continue assessment" hands to the tool to reopen it. Absent on
   *  the preview fixture, which is not a real ticket. */
  caseId?: string;
  /** A classification was actually run. A ticket saved before one (just the specs and use) must not
   *  speak as if the product were assessed: no "uncertain" regime, no verification verdict, no
   *  "no open items". */
  classified: boolean;
  regime: 'ITAR' | 'EAR-listed' | 'EAR99' | 'uncertain';
  /** Sub-caption under the regime verdict cell — depends on the actual regime, not a fixed
   *  string, since "USML checked, no match" is only true for a non-ITAR outcome. Undefined
   *  when there's nothing safe to assert (e.g. regime not yet settled). */
  regimeNote?: string;
  primaryControl: string;
  /** What that ECCN is controlled for, as codes ("NS", "EI", "AT") in display order — empty when the
   *  CCL entry's reasons are not known. The ticket leads with the control and these, and shows the
   *  regime as a label: "EAR-listed" alone only says the item has an ECCN. */
  reasonsForControl: string[];
  destination: string;
  /** What the product is for — Stage 1 (Product Specs & Use) asks for it. Undefined when the tool
   *  never captured one. */
  intendedUse?: string;
  licenceRequired: boolean | null;
  /** A licensing check actually ran (asked for, or run by the report). The licence verdict and the
   *  Licensing panel are only shown then — a destination is optional, and "Uncertain" over a check
   *  nobody ran reads as an inconclusive result. */
  licensingRan: boolean;
  /** What the regime MEANS — which agency licenses it and what to assume. Review feedback:
   *  a regime name alone "is not enough - especially for engineers". */
  licensingImplication: string;
  confidence: string;
  executiveSummary: string[];
  /** The Final Design Summary (Stage 4) was generated — state.report exists. Everything below that
   *  comes from the report (executive summary, next step, caveats, options, ladder) is only
   *  present, and only shown, when this is true. */
  hasReport: boolean;
  recommendedNextStep?: string;
  reportCaveats: string[];
  /** The report's one-or-two-sentence gloss on the licensing result, when a licensing check ran. */
  licensingSummary?: string;
  designChanges: TicketDesignChanges | null;
  specs: TicketSpec[];
  orderOfReview: Array<{ title: string; body: string; tag?: string }>;
  openItems: TicketOpenItem[];
  unavailableSpecs: string[];
  constraints: TicketConstraint[];
  /** Whether a verifier turn actually ran at all — distinct from verifierSupported, which is
   *  meaningless (defaults false) when it didn't. Without this, an assessment with no
   *  verifier reads as "Verification raised issues" instead of "Not independently verified". */
  verifierRan: boolean;
  verifierSupported: boolean;
  verifierNote: string;
  options: TicketOption[];
  optionsNote?: string;
  ladder: TicketRung[];
  licensingBasis: Array<{ label: string; value: string }>;
  documents: Array<{ name: string; meta: string }>;
  trail: Array<{ time: string; text: string }>;
  /** The chat, in order: what the user asked and what the assistant answered. From the chat stored on
   *  the ticket when there is one, else from the server's own record of the turns (tickets saved before
   *  the chat was stored). Empty when the ticket has neither. */
  conversation: TicketChatMessage[];
  supersededNote?: string;
  regulationEdition?: string;
  /** For the PDF cover's "Prepared by" row (exportDesignTicketReportPDF in EntityTicketPdf.ts) —
   *  matches every other docType's report data shape. */
  preparedBy?: string;
}

const REGIME_CLASS: Record<string, string> = {
  ITAR: 'regime-itar',
  'EAR-listed': 'regime-ear-listed',
  EAR99: 'regime-ear99',
  uncertain: 'regime-uncertain',
};

// Mirrors _LICENSING_IMPLICATION in pyapi/app/workflow/export_design_workflow.py, which is
// the source of truth and already puts this in the chat answer. Duplicated here because the
// ticket renders from a saved case and the backend does not yet carry the text into the
// report payload — once it does, delete this and read it off the payload instead. Any edit
// to the wording has to be made in both places until then.
export const LICENSING_IMPLICATION: Record<string, string> = {
  ITAR:
    "ITAR items are licensed by the U.S. Department of State's Directorate of Defense Trade " +
    'Controls (DDTC), not Commerce — assume an authorisation is required for any destination, ' +
    'typically a DSP-5 for hardware or technical data, or a TAA/MLA for a defense service. ' +
    'Your company must be registered with DDTC before it can apply. Releasing technical data ' +
    'to a foreign person counts as an export even if nothing physically leaves the building.',
  'EAR-listed':
    'Because this design is described by an ECCN on the Commerce Control List, it is licensed ' +
    'by the U.S. Department of Commerce through the Bureau of Industry and Security (BIS), ' +
    'with applications filed in SNAP-R. Whether an individual licence is needed for a given ' +
    "shipment depends on where it is going: the ECCN's reasons for control are read against " +
    'the Commerce Country Chart, and a licence exception may apply instead of a licence.',
  EAR99:
    'EAR99 items are subject to the EAR but are not listed on the Commerce Control List, so ' +
    'most shipments to most destinations need no licence. One is still required for an ' +
    'embargoed or sanctioned destination, for a denied or restricted party, or for a ' +
    'prohibited end use — certain military, nuclear, missile and chemical/biological ' +
    'applications among them. Screen the destination, the end user and the end use before shipping.',
  uncertain:
    'Until the regime is settled, treat the design as potentially controlled and hold off on ' +
    'shipping it or sharing technical data. The licensing authority differs by regime — ' +
    'State/DDTC for ITAR, Commerce/BIS for a CCL entry — so this has to be resolved first.',
};

const licenceVerdict = (v: boolean | null): string =>
  v === true ? 'Yes' : v === false ? 'No' : 'Uncertain';

// Scroll block with top/bottom fades. The wrapper owns the fades and the inner box owns the
// overflow, so a fade sits OVER the content instead of scrolling away with it. Mirrors
// BoScrollBlock in BeneficialOwnershipTicketResults.tsx — an INDEPENDENT copy, not shared
// code. If a third ticket ever wants this, extract a shared component rather than pasting a
// third implementation (flagged for senior devs, see export-design-ticket.css header).
const TicketScroll: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [hasAbove, setHasAbove] = useState(false);
  const [hasBelow, setHasBelow] = useState(false);

  // A 4px dead-band on all three comparisons absorbs sub-pixel rounding, so a box that
  // fits by half a pixel never flickers a fade.
  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const overflowing = el.scrollHeight - el.clientHeight > 4;
    setHasAbove(overflowing && el.scrollTop > 4);
    setHasBelow(
      overflowing && el.scrollHeight - el.scrollTop - el.clientHeight > 4,
    );
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    let observer: ResizeObserver | undefined = undefined;
    if (typeof (window as any).ResizeObserver !== 'undefined' && scrollRef.current) {
      observer = new ResizeObserver(measure);
      observer.observe(scrollRef.current);
      // Observing the child too catches content changing height without a scroll or a
      // resize of the box itself.
      if (scrollRef.current.firstElementChild) {
        observer.observe(scrollRef.current.firstElementChild);
      }
    }
    return () => {
      window.removeEventListener('resize', measure);
      if (observer) observer.disconnect();
    };
  }, [measure, children]);

  const cls = [
    'da-ticket-scroll-wrap',
    hasAbove ? 'has-above' : '',
    hasBelow ? 'has-below' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cls}>
      <div
        className='da-ticket-scroll'
        ref={scrollRef}
        onScroll={measure}
        tabIndex={0}
        role='region'
        aria-label={label}
      >
        {children}
      </div>
    </div>
  );
};

// Reopens this saved assessment in the live tool — the Design Assistant's counterpart of the Export
// Workflow's "Continue Workflow" button (ConsolidatedResults.tsx), with the same markup and the same
// hand-off: navigate to the tool with the saved ticket in router state. ExportDesignChatUI reads
// `resumeDesignTicketId`, loads the case and restores the session. A separate component so
// useHistory only runs where a Router exists (the embedded ticket page), not in the preview fixture.
const ContinueAssessmentButton: React.FC<{ caseId: string }> = ({ caseId }) => {
  const history = useHistory();
  return (
    <div className='results-card-frame' style={{ paddingBottom: '0px', gap: '12px' }}>
      <button
        className='btn-new-automate'
        type='button'
        onClick={() =>
          history.push({ pathname: '/export-design', state: { resumeDesignTicketId: caseId } })
        }
      >
        <div>Continue assessment</div>
      </button>
    </div>
  );
};

// --- The Conversation panel: the saved chat, read-only.

// react-markdown and remark-gfm are ES-only packages, so they are loaded when a message is first drawn
// instead of being imported here: this module is also imported by code that never draws a conversation
// (the payload reader, the PDF). Until they load — or if they cannot — the message shows as plain text,
// which is still readable.
const TicketMarkdown: React.FC<{ text: string }> = ({ text }) => {
  const [markdown, setMarkdown] = useState<{ Render: React.ComponentType<any>; gfm: unknown } | null>(null);
  useEffect(() => {
    let active = true;
    Promise.all([import('react-markdown'), import('remark-gfm')])
      .then(([renderer, gfm]) => {
        if (active) setMarkdown({ Render: renderer.default as React.ComponentType<any>, gfm: gfm.default });
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);
  if (!markdown) return <p className='da-ticket-chat-plain'>{text}</p>;
  return <markdown.Render remarkPlugins={[markdown.gfm]}>{text}</markdown.Render>;
};

// A message longer than this is collapsed to a few lines with a Show more control, so one long answer
// cannot push the rest of the conversation out of sight.
const CHAT_CLAMP_CHARS = 700;

const formatChatTime = (ts: string): string => {
  const date = new Date(ts);
  if (!ts || Number.isNaN(date.getTime())) return '';
  // 24-hour, like the trail's "HH:MM" above it, so the two read alike (and never "24:05" at midnight).
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
};

const ConversationPanel: React.FC<{
  messages: TicketChatMessage[];
  sectionRef: (node: HTMLElement | null) => void;
}> = ({ messages, sectionRef }) => {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggle = (index: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  return (
    <section className='da-ticket-panel' ref={sectionRef}>
      <div className='da-ticket-panel-heading'>
        <ChatBubbleOutlineIcon sx={{ fontSize: 19 }} />
        <div className='da-ticket-panel-heading-text'>
          <h3>Conversation</h3>
          <p>What was asked, and what the assistant answered.</p>
        </div>
        <span className='da-ticket-count-pill'>{messages.length}</span>
      </div>
      <TicketScroll label='Conversation'>
        <ol className='da-ticket-chat'>
          {messages.map((message, index) => {
            const time = formatChatTime(message.ts);
            const isLong = !message.isReport && message.text.length > CHAT_CLAMP_CHARS;
            const isOpen = expanded.has(index);
            return (
              <li key={index} className={`da-ticket-chat-msg is-${message.sender}`}>
                <div className='da-ticket-chat-meta'>
                  <b>{message.sender === 'user' ? 'You' : 'Design Assistant'}</b>
                  {time && <time dateTime={message.ts}>{time}</time>}
                </div>
                {message.files && message.files.length > 0 && (
                  <div className='da-ticket-chat-files'>
                    {message.files.map((file, fileIndex) => (
                      <span key={`${file}-${fileIndex}`} className='da-ticket-chat-file'>
                        <DescriptionOutlinedIcon sx={{ fontSize: 14 }} />
                        {file}
                      </span>
                    ))}
                  </div>
                )}
                {message.isReport ? (
                  <p className='da-ticket-chat-note'>Final Design Summary — shown in full above.</p>
                ) : (
                  <div className={`da-ticket-chat-body${isLong && !isOpen ? ' is-clamped' : ''}`}>
                    {message.sender === 'user' ? (
                      <p className='da-ticket-chat-plain'>{message.text}</p>
                    ) : (
                      <TicketMarkdown text={message.text} />
                    )}
                  </div>
                )}
                {isLong && (
                  <button
                    type='button'
                    className='da-ticket-chat-toggle'
                    aria-expanded={isOpen}
                    onClick={() => toggle(index)}
                  >
                    {isOpen ? 'Show less' : 'Show more'}
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      </TicketScroll>
    </section>
  );
};

interface ExportDesignTicketProps {
  ticket: DesignTicketPayload;
  /** True (default) for the standalone /export-design/ticket-preview fixture route, which has
   *  no real page chrome around it and needs this component to fake one. False when embedded
   *  in the real /monitor/ticket/:id page (pages/monitor/Ticket.tsx), where AuditHeaderLayout
   *  and that page's own tab bar already provide real chrome — rendering this component's own
   *  mock banner/title/tabs on top of genuinely saved data would duplicate them. */
  isPreview?: boolean;
  /** Only meaningful when isPreview is false: the same triggerCSV/setTriggerCSV state every
   *  other docType's results component watches, wired to AuditHeaderLayout's Download button
   *  via pages/monitor/Ticket.tsx. */
  triggerCSV?: boolean;
  setTriggerCSV?: (value: boolean) => void;
}

const ExportDesignTicket: React.FC<ExportDesignTicketProps> = ({
  ticket,
  isPreview = true,
  triggerCSV,
  setTriggerCSV,
}) => {
  // Mirrors every other results component's own triggerCSV effect (e.g.
  // ResultsTab/Results.tsx, ConsolidatedResults.tsx): the shared chrome's Download button
  // just flips this flag, so it does nothing unless something here answers it.
  useEffect(() => {
    if (!isPreview && triggerCSV) {
      exportDesignTicketReportPDF(ticket, ticket.ticketName);
      setTriggerCSV?.(false);
    }
  }, [triggerCSV]);

  // A destination is optional, and the payload uses '—' when there isn't one.
  const hasDestination = Boolean(ticket.destination) && ticket.destination !== '—';
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const registerSection = (key: string) => (node: HTMLElement | null) => {
    sectionRefs.current[key] = node;
  };
  // Jumps scroll by ref rather than a #hash href, so an in-page navigation never writes to
  // the router's location.
  const scrollToSection = (key: string) => () => {
    const node = sectionRefs.current[key];
    if (node && typeof node.scrollIntoView === 'function') {
      const reduced =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      node.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    }
  };

  const openCount = ticket.openItems.length;
  // Before a classification there is nothing to have assumed — unless the user already said a spec
  // is unavailable, which is real and worth keeping.
  const showOpenItems = ticket.classified || openCount > 0 || ticket.unavailableSpecs.length > 0;
  const decontrollable = ticket.constraints.filter(
    (c) => c.decontrol && !/^none/i.test(c.decontrol),
  ).length;

  // What was actually produced. Only the guided stages are always shown; licensing, design
  // changes, options and the ladder appear when a check/analysis ran or the report generated them.
  const hasLicensing = ticket.licensingRan;
  const hasOptions = ticket.options.length > 0;
  const hasLadder = ticket.ladder.length > 0;
  const hasDesignChanges = Boolean(ticket.designChanges);
  const hasCaveats = ticket.reportCaveats.length > 0;
  const verificationLabel = !ticket.verifierRan
    ? 'Not independently verified'
    : ticket.verifierSupported
    ? 'Independently verified'
    : 'Verification raised issues';
  // Whatever was not produced is left out rather than shown as an empty or "not generated" slot:
  // no tile, no verdict cell, no panel. A clean result with no binding controls has no
  // Specification Map to show, and a ticket saved before the report has no summary tile.
  const hasMap = ticket.constraints.length > 0;
  const hasReference =
    ticket.documents.length > 0 ||
    ticket.trail.length > 0 ||
    Boolean(ticket.supersededNote || ticket.regulationEdition);
  const confidenceKnown = Boolean(ticket.confidence) && ticket.confidence !== 'Not stated';
  const statTiles = 2 + (hasMap ? 1 : 0) + (confidenceKnown ? 1 : 0);
  // The control leads (EAR99 has none of its own, so it would only repeat the regime).
  const hasControl = Boolean(ticket.primaryControl) && ticket.primaryControl !== '—' && ticket.primaryControl !== ticket.regime;
  const reasonCodes = ticket.reasonsForControl || [];
  const verdictCells = 1 + (hasControl ? 1 : 0) + (ticket.intendedUse ? 1 : 0) + (hasLicensing ? 1 : 0);
  const hasConversation = ticket.conversation.length > 0;
  const jumps: Array<[string, string]> = [
    ['specs', 'Specs & use'],
    ['determination', 'Classification'],
    ...(openCount > 0 || ticket.unavailableSpecs.length > 0
      ? ([['open-items', 'Open items']] as Array<[string, string]>)
      : []),
    ...(hasMap ? ([['grounding', 'Specification map']] as Array<[string, string]>) : []),
    ...(hasCaveats ? ([['caveats', 'Caveats']] as Array<[string, string]>) : []),
    ...(hasLicensing ? ([['licensing', 'Licensing']] as Array<[string, string]>) : []),
    ...(hasDesignChanges ? ([['design-changes', 'Design changes']] as Array<[string, string]>) : []),
    ...(hasOptions ? ([['options', 'Options']] as Array<[string, string]>) : []),
    ...(hasLadder ? ([['ladder', 'Ladder']] as Array<[string, string]>) : []),
    ...(hasConversation ? ([['conversation', 'Conversation']] as Array<[string, string]>) : []),
  ];
  const hasExtras = hasCaveats || hasLicensing || hasDesignChanges || hasOptions || hasLadder;

  return (
    <div className={`da-ticket-shell ${REGIME_CLASS[ticket.regime] || ''}`}>
      {isPreview && (
        <div className='da-ticket-mock'>
          <b>Design mockup</b>
          <span>
            Nothing is saved or loaded — this renders from a fixture. Fields marked{' '}
            <span className='da-ticket-tag is-proposed'>proposed</span> would need backend
            work.
          </span>
        </div>
      )}

      {isPreview ? (
        <>
          {/* --- page chrome (mirrors AuditHeaderLayout on /monitor/ticket/:id) — only
              rendered in the standalone preview, which has no real chrome around it. Embedded
              in the real ticket page, AuditHeaderLayout + its own tab bar already provide
              this, so rendering it again here would show a duplicate title and tab bar. --- */}
          <div className='da-ticket-chrome'>
            <div>
              <h1>{ticket.ticketName}</h1>
              <div className='da-ticket-chrome-meta'>
                <span>
                  <b>Owner</b> {ticket.owner}
                </span>
                <span>
                  <b>Team</b> {ticket.team}
                </span>
                <span>
                  <b>Shared with</b> {ticket.sharedWith} people
                </span>
                {ticket.reference && (
                  <span>
                    <span className='da-ticket-tag is-proposed'>proposed</span> Ref{' '}
                    {ticket.reference}
                  </span>
                )}
              </div>
            </div>
            <div className='da-ticket-actions'>
              {/* Preview-only mock of the button. The real one is ContinueAssessmentButton, shown
                  by the embedded branch below on a saved ticket. */}
              <button className='btn-primary' type='button'>
                Continue assessment
              </button>
              <button
                className='btn-outline'
                type='button'
                onClick={() => exportDesignTicketReportPDF(ticket, ticket.ticketName)}
              >
                Download PDF
              </button>
              <button className='btn-outline' type='button'>
                Share
              </button>
            </div>
          </div>

          <div className='da-ticket-tabs' role='tablist'>
            {['Results', 'Documents', 'Activity', 'Settings'].map((tab, i) => (
              <button
                key={tab}
                className='da-ticket-tab'
                role='tab'
                type='button'
                aria-selected={i === 0}
              >
                {tab}
                {tab === 'Documents' && <span>{ticket.documents.length}</span>}
              </button>
            ))}
          </div>
        </>
      ) : (
        // Embedded in the real ticket page: Download/Share/Favorite/Delete are chrome-level, already
        // provided by AuditHeaderLayout, so the only action this adds is the one that is specific to
        // the tool — Continue assessment, as Export Workflow's "Continue Workflow" does. Only on a
        // real saved ticket: it needs the case id.
        ticket.caseId ? <ContinueAssessmentButton caseId={ticket.caseId} /> : null
      )}

      {/* --- metadata strip: same position, same fields (Saved/Frequency/Status/Ticket Type)
          as Export Workflow's own metadata strip (ConsolidatedResults.tsx:2420-2492) — a
          plain card row below the tabs, not folded into the title as BO's ticket does it
          (BO omits this strip entirely). "Last Screen" doesn't apply to a one-shot
          assessment, so this uses "Saved" instead, but keeps the same 4-card structure. */}
      <div className='da-ticket-meta-grid'>
        <div className='da-ticket-meta-card'>
          <span>Saved</span>
          <strong>{ticket.savedAt}</strong>
        </div>
        <div className='da-ticket-meta-card'>
          <span>Frequency</span>
          {/* Design Assistant is a one-shot assessment, never re-screened on a
              schedule -- same as Export Workflow's own "Screening Frequency"
              card (ConsolidatedResults.tsx), which shows N/A for the same
              reason regardless of whatever `frequency` the ticket stored. */}
          <strong>N/A</strong>
        </div>
        <div className='da-ticket-meta-card'>
          <span>Status</span>
          <strong className={`da-ticket-status${ticket.status === 'Complete' ? ' is-clear' : ''}`}>
            <i />
            {ticket.status}
          </strong>
        </div>
        <div className='da-ticket-meta-card'>
          <span>Ticket Type</span>
          <strong>{ticket.ticketType}</strong>
        </div>
      </div>

      {/* --- KPI stats: one tile per stage of the guided flow --- */}
      <div className={`da-ticket-stat-grid${statTiles < 4 ? ` is-${statTiles}` : ''}`}>
        <button className='da-ticket-stat-card' type='button' onClick={scrollToSection('specs')}>
          <span>
            <TuneOutlinedIcon sx={{ fontSize: 18 }} />
            Product specs &amp; use
          </span>
          <strong>{ticket.specs.length}</strong>
          {ticket.intendedUse && <small>{ticket.intendedUse}</small>}
        </button>
        <button
          className='da-ticket-stat-card'
          type='button'
          onClick={scrollToSection('determination')}
        >
          <span>
            <ShieldOutlinedIcon sx={{ fontSize: 18 }} />
            Classification
          </span>
          {/* The control and what it is controlled for lead; the regime is the label under them. */}
          <strong className='is-text'>
            {!ticket.classified ? 'Not run yet' : hasControl ? ticket.primaryControl : ticket.regime}
          </strong>
          {hasControl && (
            <small>
              {ticket.regime}
              {reasonCodes.length > 0 ? ` · ${reasonCodes.join(', ')}` : ''}
            </small>
          )}
        </button>
        {hasMap && (
          <button
            className='da-ticket-stat-card'
            type='button'
            onClick={scrollToSection('grounding')}
          >
            <span>
              <FactCheckOutlinedIcon sx={{ fontSize: 18 }} />
              Specification map
            </span>
            <strong>
              {ticket.constraints.length}
              {/* Only when it ADDS something — a sub-count equal to the total just repeats the
                  number beside it. */}
              {decontrollable > 0 && decontrollable < ticket.constraints.length && (
                <em>{decontrollable} decontrollable</em>
              )}
            </strong>
            <small>{verificationLabel}</small>
          </button>
        )}
        {confidenceKnown && (
          <button
            className='da-ticket-stat-card'
            type='button'
            onClick={scrollToSection(hasMap ? 'grounding' : 'determination')}
          >
            <span>
              <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
              Confidence
            </span>
            <strong className='is-text'>{ticket.confidence}</strong>
            <small>{ticket.hasReport ? 'Overall, from the summary' : 'Of the classification'}</small>
          </button>
        )}
      </div>

      {/* --- hero --- */}
      <section className='da-ticket-exec' ref={registerSection('exec') as any}>
        <div className='da-ticket-eyebrow'>
          <SummarizeOutlinedIcon sx={{ fontSize: 16 }} />
          Saved Design Assessment
        </div>
        <h2>{ticket.productName}</h2>

        <div className={`da-ticket-verdict${verdictCells < 4 ? ` is-${verdictCells}` : ''}`}>
          {hasControl && (
            <div className='da-ticket-verdict-cell'>
              <span className='da-lbl'>{ticket.regime === 'ITAR' ? 'USML category' : 'ECCN'}</span>
              <span className='da-val' title={reasonCodes.length ? reasonPhrase(reasonCodes) : undefined}>
                {ticket.primaryControl}
              </span>
              {reasonCodes.length > 0 && (
                <span className='da-sub' title={reasonPhrase(reasonCodes)}>
                  Controlled for {reasonCodes.join(', ')}
                </span>
              )}
              {hasMap && (
                <span className='da-sub'>
                  {ticket.constraints.length} binding control{ticket.constraints.length === 1 ? '' : 's'}
                </span>
              )}
            </div>
          )}
          <div className='da-ticket-verdict-cell'>
            <span className='da-lbl'>Regime</span>
            <span className='da-val is-regime'>
              {ticket.classified ? ticket.regime : 'Not classified yet'}
            </span>
            {ticket.regimeNote && <span className='da-sub'>{ticket.regimeNote}</span>}
          </div>
          {ticket.intendedUse && (
            <div className='da-ticket-verdict-cell'>
              <span className='da-lbl'>Intended use</span>
              <span className='da-val is-prose'>{ticket.intendedUse}</span>
            </div>
          )}
          {/* Only when a licensing check ran: a destination is optional, and "Uncertain" over a
              check nobody asked for reads as an inconclusive result. */}
          {hasLicensing && (
            <div className='da-ticket-verdict-cell'>
              <span className='da-lbl'>Licence required</span>
              <span
                className={`da-val${ticket.licenceRequired === false ? ' is-clear' : ''}`}
              >
                {licenceVerdict(ticket.licenceRequired)}
              </span>
              <span className='da-sub'>
                {hasDestination ? `To ${ticket.destination}` : 'On the inputs checked'}
              </span>
            </div>
          )}
        </div>

        {/* The summary the report generated. This ticket IS the saved summary, so it leads the page
            under no heading of its own — and the action it recommends sits right under it. */}
        {ticket.executiveSummary.length > 0 && (
          <div className='da-ticket-exec-prose'>
            {ticket.executiveSummary.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        )}
        {ticket.recommendedNextStep && (
          <div className='da-ticket-next-step'>
            <span className='da-lbl'>Recommended next step</span>
            <p>{ticket.recommendedNextStep}</p>
          </div>
        )}

        {/* A regime is a category, not an instruction. This is the "so what", directly under
            the verdict so it is read before anything else on the page. It is the general rule for
            the regime; any destination-specific answer is in the Licensing panel, when a check
            was run. */}
        {ticket.licensingImplication && (
          <div className='da-ticket-implication'>
            <span className='da-lbl'>What this means for licensing</span>
            <p>{ticket.licensingImplication}</p>
          </div>
        )}

        <nav className='da-ticket-jumps'>
          <span className='da-ticket-jumps-label'>Jump to</span>
          {jumps.map(([key, label]) => (
            <button
              key={key}
              className='da-ticket-jump'
              type='button'
              onClick={scrollToSection(key)}
            >
              {label}
            </button>
          ))}
        </nav>
      </section>

      {/* --- Stage 1 | Stage 2: product specs & use | classification --- */}
      <div className='da-ticket-row da-ticket-row-a'>
        <section className='da-ticket-panel' ref={registerSection('specs') as any}>
          <div className='da-ticket-panel-heading'>
            <TuneOutlinedIcon sx={{ fontSize: 19 }} />
            <div className='da-ticket-panel-heading-text'>
              <h3>Product specs &amp; use</h3>
              <p>
                {ticket.classified
                  ? 'Frozen at save — the classification is only valid for these values.'
                  : 'What you have told the tool so far.'}
              </p>
            </div>
            <span className='da-ticket-count-pill'>{ticket.specs.length}</span>
          </div>
          <TicketScroll label='Product specs and use'>
            <table className='da-ticket-table'>
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className='da-key'>Intended use</td>
                  <td>
                    {ticket.intendedUse || 'Not disclosed'}
                    <span className='da-ticket-src'>
                      {ticket.intendedUse ? 'stated by you' : 'none given'}
                    </span>
                  </td>
                </tr>
                {hasDestination && (
                  <tr>
                    <td className='da-key'>Destination country</td>
                    <td>
                      {ticket.destination}
                      <span className='da-ticket-src'>optional — used for licensing advice</span>
                    </td>
                  </tr>
                )}
                {ticket.specs.map((spec) => (
                  <tr key={spec.label}>
                    <td className='da-key'>{spec.label}</td>
                    <td>
                      {spec.value}
                      {spec.source && <span className='da-ticket-src'>{spec.source}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TicketScroll>
        </section>

        <section className='da-ticket-panel' ref={registerSection('determination') as any}>
          <div className='da-ticket-panel-heading'>
            <ShieldOutlinedIcon sx={{ fontSize: 19 }} />
            <div className='da-ticket-panel-heading-text'>
              <h3>Classification</h3>
              <p>
                {ticket.classified ? 'Order of review: USML → CCL → EAR99, in that order.' : 'Not run yet.'}
              </p>
            </div>
          </div>
          {ticket.classified ? (
            <TicketScroll label='Classification and Order of Review'>
              <div>
                {ticket.orderOfReview.map((step, index) => (
                  <div className='da-ticket-item' key={`${step.title}-${index}`}>
                    <h4>
                      {step.title}
                      {step.tag && <span className='da-ticket-tag is-clear'>{step.tag}</span>}
                    </h4>
                    <p>{step.body}</p>
                  </div>
                ))}
                {/* Verifies the classification itself, so it lives here — and survives a clean
                    result with no binding controls (no Specification Map to hang it on). */}
                <div className='da-ticket-verify'>
                  <CheckCircleOutlineIcon sx={{ fontSize: 17 }} />
                  <div>
                    <b>{verificationLabel}.</b> {ticket.verifierNote}
                  </div>
                </div>
              </div>
            </TicketScroll>
          ) : (
            // Saved before a classification was run: there is no determination, no verification and
            // no assumptions to report, so say so rather than show them as empty or as clear.
            <div className='da-ticket-empty'>
              This product has not been classified yet. Continue the assessment to classify it, then
              save it again.
            </div>
          )}
        </section>
      </div>

      {/* --- Stage 2 (assumptions) | Stage 3: open items | specification map (when there are
          binding controls to map). Left out entirely before a classification: "no open items" over
          a product nobody classified would claim an assessment that never happened. --- */}
      {showOpenItems && (
      <div className={`da-ticket-row da-ticket-row-b${hasMap ? '' : ' da-ticket-row-solo'}`}>
        <section className='da-ticket-panel' ref={registerSection('open-items') as any}>
          <div className='da-ticket-panel-heading'>
            <ReportProblemOutlinedIcon sx={{ fontSize: 19 }} />
            <div className='da-ticket-panel-heading-text'>
              <h3>Open items</h3>
              <p>Specs the classification assumed. Resolve, or knowingly accept.</p>
            </div>
            {openCount > 0 && (
              <span className='da-ticket-count-pill is-open'>{openCount} unresolved</span>
            )}
          </div>
          {openCount || ticket.unavailableSpecs.length ? (
            <TicketScroll label='Open items'>
              <div>
                {ticket.openItems.map((item, index) => (
                  <div className='da-ticket-item' key={`${item.missingSpec}-${index}`}>
                    <h4>
                      {item.missingSpec}
                      {item.couldRaise && (
                        <span className='da-ticket-tag is-open'>could raise control</span>
                      )}
                    </h4>
                    <dl>
                      <dt>Assumed</dt>
                      <dd>{item.assumed}</dd>
                      <dt>Impact</dt>
                      <dd>{item.impact}</dd>
                    </dl>
                  </div>
                ))}
                {ticket.unavailableSpecs.length > 0 && (
                  <div className='da-ticket-item'>
                    <h4>Declared unavailable</h4>
                    <p>
                      You told the tool you can&apos;t supply{' '}
                      {ticket.unavailableSpecs.join(', ')} — recorded so it never asks again.
                    </p>
                  </div>
                )}
              </div>
            </TicketScroll>
          ) : (
            <div className='da-ticket-empty'>
              No open items — every control-relevant spec was supplied.
            </div>
          )}
        </section>

        {hasMap && (
        <section className='da-ticket-panel' ref={registerSection('grounding') as any}>
          <div className='da-ticket-panel-heading'>
            <FactCheckOutlinedIcon sx={{ fontSize: 19 }} />
            <div className='da-ticket-panel-heading-text'>
              <h3>Specification map</h3>
              <p>Which of your specs drive each control — every clause quoted from the retrieved text.</p>
            </div>
            <span className='da-ticket-count-pill'>{ticket.constraints.length}</span>
          </div>
          <TicketScroll label='Specification map'>
            <div>
              <table className='da-ticket-table'>
                <thead>
                  <tr>
                    <th>Control</th>
                    <th>Your spec</th>
                    <th>What would decontrol it</th>
                  </tr>
                </thead>
                <tbody>
                  {ticket.constraints.map((c, index) => (
                    <tr key={`${c.citation}-${index}`}>
                      <td className='da-code'>{c.citation}</td>
                      <td className='da-spec'>{c.matchedSpec}</td>
                      <td>{c.decontrol}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {ticket.constraints.map((c) => (
                <div className='da-ticket-item' key={`${c.citation}-text`}>
                  <h4>{c.citation} — quoted</h4>
                  <p className='da-ticket-quote'>{c.controlText}</p>
                </div>
              ))}
            </div>
          </TicketScroll>
        </section>
        )}
      </div>
      )}

      {/* --- Stage 4 and anything generated with / asked for after it. Every panel here is
          conditional: a ticket that stopped at the Specification Map renders none of this, and a
          panel never appears empty. --- */}
      {hasExtras && (
        <div className='da-ticket-row da-ticket-row-c da-ticket-row-auto'>
          {hasCaveats && (
            <section className='da-ticket-panel' ref={registerSection('caveats') as any}>
              <div className='da-ticket-panel-heading'>
                <ReportProblemOutlinedIcon sx={{ fontSize: 19 }} />
                <div className='da-ticket-panel-heading-text'>
                  <h3>Caveats</h3>
                  <p>Gaps and assumptions the summary could not settle.</p>
                </div>
                <span className='da-ticket-count-pill'>{ticket.reportCaveats.length}</span>
              </div>
              <TicketScroll label='Caveats'>
                <ul className='da-ticket-list'>
                  {ticket.reportCaveats.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </TicketScroll>
            </section>
          )}

          {hasLicensing && (
            <section className='da-ticket-panel' ref={registerSection('licensing') as any}>
              <div className='da-ticket-panel-heading'>
                <GavelOutlinedIcon sx={{ fontSize: 19 }} />
                <div className='da-ticket-panel-heading-text'>
                  <h3>{hasDestination ? `Licensing — ${ticket.destination}` : 'Licensing'}</h3>
                  <p>What this classification means for shipping it there.</p>
                </div>
                <span
                  className={`da-ticket-tag${
                    ticket.licenceRequired === false ? ' is-clear' : ' is-open'
                  }`}
                >
                  {ticket.licenceRequired === false
                    ? 'no licence required'
                    : ticket.licenceRequired === true
                    ? 'licence required'
                    : 'uncertain'}
                </span>
              </div>
              <TicketScroll label='Licensing'>
                <div>
                  {ticket.licensingSummary && (
                    <div className='da-ticket-item'>
                      <p>{ticket.licensingSummary}</p>
                    </div>
                  )}
                  <div className='da-ticket-item'>
                    <dl>
                      {ticket.licensingBasis.map((b, i) => (
                        <React.Fragment key={`${b.label}-${i}`}>
                          <dt>{b.label}</dt>
                          <dd>{b.value}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  </div>
                </div>
              </TicketScroll>
            </section>
          )}

          {ticket.designChanges && (
            <section className='da-ticket-panel' ref={registerSection('design-changes') as any}>
              <div className='da-ticket-panel-heading'>
                <TuneOutlinedIcon sx={{ fontSize: 19 }} />
                <div className='da-ticket-panel-heading-text'>
                  <h3>Design changes{ticket.designChanges.target ? ` — to ${ticket.designChanges.target}` : ''}</h3>
                  <p>An analysis you asked for; not part of the guided stages.</p>
                </div>
                <span
                  className={`da-ticket-tag${ticket.designChanges.reachable ? ' is-clear' : ' is-open'}`}
                >
                  {ticket.designChanges.reachable ? 'reachable' : 'not reachable by design'}
                </span>
              </div>
              <TicketScroll label='Design changes'>
                <div>
                  {ticket.designChanges.summary && (
                    <div className='da-ticket-item'>
                      <p>{ticket.designChanges.summary}</p>
                    </div>
                  )}
                  {ticket.designChanges.changes.map((ch, i) => (
                    <div className='da-ticket-item' key={`${ch.description}-${i}`}>
                      <h4>{ch.description}</h4>
                      {ch.details.length > 0 && (
                        <ul className='da-ticket-list'>
                          {ch.details.map((d, j) => (
                            <li key={j}>{d}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                  {ticket.designChanges.resultingRegime && (
                    <div className='da-ticket-item'>
                      <h4>Result</h4>
                      <p>The changed product would be {ticket.designChanges.resultingRegime}.</p>
                    </div>
                  )}
                  {ticket.designChanges.cost && (
                    <div className='da-ticket-item'>
                      <h4>What it costs</h4>
                      <p>{ticket.designChanges.cost}</p>
                    </div>
                  )}
                  {ticket.designChanges.residual.length > 0 && (
                    <div className='da-ticket-item'>
                      <h4>Still binding afterwards</h4>
                      <ul className='da-ticket-list'>
                        {ticket.designChanges.residual.map((r, i) => (
                          <li key={i}>{r}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </TicketScroll>
            </section>
          )}

          {hasOptions && (
            <section className='da-ticket-panel' ref={registerSection('options') as any}>
              <div className='da-ticket-panel-heading'>
                <TuneOutlinedIcon sx={{ fontSize: 19 }} />
                <div className='da-ticket-panel-heading-text'>
                  <h3>Design options</h3>
                  <p>Ranked, recommended first.</p>
                </div>
                <span className='da-ticket-count-pill'>{ticket.options.length}</span>
              </div>
              <TicketScroll label='Design options'>
                <div>
                  {ticket.options.map((opt, index) => (
                    <div
                      className={`da-ticket-option${opt.recommended ? ' is-recommended' : ''}`}
                      key={`${opt.label}-${index}`}
                    >
                      <div className='da-ticket-option-head'>
                        {opt.label}
                        {opt.recommended && (
                          <span className='da-ticket-tag is-clear'>Recommended</span>
                        )}
                      </div>
                      <div className='da-ticket-option-line'>
                        <b>Reaches</b> {opt.targetRegime}
                      </div>
                      <div className='da-ticket-option-line'>
                        <b>Change</b> {opt.changeSummary}
                      </div>
                      {opt.feasibility && (
                        <div className='da-ticket-option-line'>
                          <b>Feasibility</b> {opt.feasibility}
                        </div>
                      )}
                      {opt.tradeoff && (
                        <div className='da-ticket-option-line'>
                          <b>Trade-off</b> {opt.tradeoff}
                        </div>
                      )}
                    </div>
                  ))}
                  {ticket.optionsNote && (
                    <div className='da-ticket-empty'>{ticket.optionsNote}</div>
                  )}
                </div>
              </TicketScroll>
            </section>
          )}

          {hasLadder && (
            <section className='da-ticket-panel' ref={registerSection('ladder') as any}>
              <div className='da-ticket-panel-heading'>
                <StairsOutlinedIcon sx={{ fontSize: 19 }} />
                <div className='da-ticket-panel-heading-text'>
                  <h3>Regime ladder</h3>
                  <p>Most controlled to least.</p>
                </div>
              </div>
              <TicketScroll label='Regime ladder'>
                <div>
                  {ticket.ladder.map((rung, index) => {
                    const state = rung.isCurrent
                      ? 'is-current'
                      : rung.reachable === false
                      ? 'is-blocked'
                      : 'is-reachable';
                    return (
                      <div className={`da-ticket-rung ${state}`} key={`${rung.regime}-${index}`}>
                        <i />
                        <div>
                          <div className='da-ticket-rung-name'>
                            {rung.regime}
                            {rung.isCurrent && (
                              <span className='da-ticket-tag is-clear'>current</span>
                            )}
                          </div>
                          <div className='da-ticket-rung-meta'>{rung.note}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TicketScroll>
            </section>
          )}
        </div>
      )}

      {/* --- reference: what this was based on --- */}
      {hasReference && (
      <div className='da-ticket-row da-ticket-row-c da-ticket-row-solo'>
        <section className='da-ticket-panel'>
          <div className='da-ticket-panel-heading'>
            <HistoryOutlinedIcon sx={{ fontSize: 19 }} />
            <div className='da-ticket-panel-heading-text'>
              <h3>Documents &amp; trail</h3>
              <p>
                {ticket.documents.length > 0
                  ? 'What this was based on, and what the session did.'
                  : 'What the session did.'}
              </p>
            </div>
            {ticket.documents.length > 0 && (
              <span className='da-ticket-count-pill'>{ticket.documents.length}</span>
            )}
          </div>
          <TicketScroll label='Documents and assessment trail'>
            <div>
              {ticket.documents.map((doc, index) => (
                <div className='da-ticket-doc' key={`${doc.name}-${index}`}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 19 }} />
                  <div>
                    <b>{doc.name}</b>
                    <small>{doc.meta}</small>
                  </div>
                </div>
              ))}
              <dl className='da-ticket-trail'>
                {ticket.trail.map((row, index) => (
                  <React.Fragment key={`${row.time}-${index}`}>
                    <dt>{row.time}</dt>
                    <dd>{row.text}</dd>
                  </React.Fragment>
                ))}
              </dl>
              {ticket.supersededNote && (
                <div className='da-ticket-item' style={{ marginTop: 8 }}>
                  <h4>
                    Superseded
                    <span className='da-ticket-tag is-proposed'>proposed</span>
                  </h4>
                  <p>{ticket.supersededNote}</p>
                </div>
              )}
              {ticket.regulationEdition && (
                <div className='da-ticket-item'>
                  <h4>
                    Regulation edition
                    <span className='da-ticket-tag is-proposed'>proposed</span>
                  </h4>
                  <p>{ticket.regulationEdition}</p>
                </div>
              )}
            </div>
          </TicketScroll>
        </section>
      </div>
      )}

      {/* --- the saved chat: what was asked and answered, read-only. Left out when there is none. */}
      {hasConversation && (
        <div className='da-ticket-row da-ticket-row-c da-ticket-row-solo'>
          <ConversationPanel
            messages={ticket.conversation}
            sectionRef={registerSection('conversation')}
          />
        </div>
      )}

      <p className='da-ticket-disclaimer'>
        This is a research tool, not a formal ruling — please consult counsel for a final
        determination (or request a CJ from State/DDTC, or a CCATS classification from BIS).
      </p>
    </div>
  );
};

export default ExportDesignTicket;
